import { Component, OnInit } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { JuanaChatService } from '../../../core/services/juana-chat.service';
import type { JuanaConversationSummary } from '../../../core/models/juana/juana.types';
import { UserService } from '../../../core/services/user.service';

/** Un bloque del historial: las conversaciones de un mismo tema. */
interface ConversationGroup {
  category: string;
  label: string;
  conversations: JuanaConversationSummary[];
}

/**
 * Orden fijo de los temas en el historial. No es alfabético a propósito: sigue
 * lo que la gente hace más seguido con Juana, para que lo frecuente quede
 * arriba sin tener que buscarlo.
 */
const CATEGORY_ORDER = [
  'repuestos',
  'agendamiento',
  'salud',
  'flota',
  'conductores',
  'beneficios',
  'finanzas',
  'cuenta',
  'general',
];

/** Acceso rápido del home: rótulo visible + mensaje que se le manda a Juana. */
interface QuickAction {
  icon: string;
  labelKey: string;
  messageKey: string;
}

/**
 * PLAN-Pitts-Juana-General-FRONT.md §7.1 — home de Juana. Reemplaza el
 * placeholder en blanco que quedó cuando los accesos rápidos y el resumen de
 * reportes se movieron a "Reportes > General".
 *
 * En cuanto hay conversación, el hero da paso a <app-agent-panel>: el ciclo
 * crear→enviar→streaming es el mismo JuanaChatService (root-provided) que usa
 * el panel de Repuestos, no una segunda implementación.
 */
@Component({
  selector: 'app-main',
  standalone: false,
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss'
})
export class MainComponent implements OnInit {
  input = '';
  /** Rail lateral con el historial de conversaciones (colapsable). */
  historyOpen = false;
  /**
   * Mientras sea true se ve la bienvenida en vez de los mensajes. Es estado de
   * PRESENTACIÓN, no de datos: la conversación viva sigue en el servicio.
   */
  showWelcome = true;

  readonly quickActions: QuickAction[] = [
    { icon: 'pi pi-box', labelKey: 'main.juana.quickPart', messageKey: 'main.juana.quickPartMsg' },
    { icon: 'pi pi-heart-fill', labelKey: 'main.juana.quickHealth', messageKey: 'main.juana.quickHealthMsg' },
    { icon: 'pi pi-wrench', labelKey: 'main.juana.quickMaintenance', messageKey: 'main.juana.quickMaintenanceMsg' },
    { icon: 'pi pi-gift', labelKey: 'main.juana.quickBenefits', messageKey: 'main.juana.quickBenefitsMsg' },
  ];

  constructor(
    public juana: JuanaChatService,
    private userService: UserService,
    private transloco: TranslocoService,
  ) {}

  ngOnInit(): void {
    // Crea o reanuda la conversación y conecta el socket (idempotente).
    void this.juana.init();
    void this.juana.loadConversations();
  }

  toggleHistory(): void {
    this.historyOpen = !this.historyOpen;
    // Se refresca al abrir para no mostrar una lista vieja si el usuario estuvo
    // conversando desde el widget en otra pantalla.
    if (this.historyOpen) void this.juana.loadConversations();
  }

  async openConversation(id: string): Promise<void> {
    if (this.juana.busy()) return;
    const ok = await this.juana.openConversation(id);
    if (ok) this.showWelcome = false;
  }

  /** "Nueva conversación" devuelve la bienvenida, no un chat vacío sin contexto. */
  async startNewConversation(): Promise<void> {
    if (this.juana.busy()) return;
    this.showWelcome = true;
    await this.juana.newConversation();
    void this.juana.loadConversations();
  }

  /** Sin título todavía = conversación abierta pero sin mensajes. */
  conversationLabel(c: { title: string | null }): string {
    return c.title?.trim() || this.transloco.translate('main.juana.untitledChat');
  }

  /** Id de la conversación que se está renombrando (null = ninguna). */
  renamingId: string | null = null;
  renameDraft = '';

  /**
   * Renombrar existe porque los títulos los redacta un modelo: cuando se
   * equivoca, el usuario tiene que poder arreglarlo sin depender de nadie.
   */
  startRename(event: MouseEvent, c: JuanaConversationSummary): void {
    // Sin esto el click abriría además la conversación que se quiere renombrar.
    event.stopPropagation();
    this.renamingId = c.id;
    this.renameDraft = c.title ?? '';
  }

  cancelRename(): void {
    this.renamingId = null;
    this.renameDraft = '';
  }

  async confirmRename(conversationId: string): Promise<void> {
    const title = this.renameDraft.trim();
    if (!title) return this.cancelRename();
    await this.juana.renameConversation(conversationId, title);
    this.cancelRename();
  }

  /**
   * Historial agrupado por tema. Las categorías conocidas van en el orden fijo
   * de arriba; las que el agente propuso por su cuenta van después, alfabéticas,
   * para que no se cuelen entre las estables cambiando el orden cada vez.
   */
  get conversationGroups(): ConversationGroup[] {
    const groups = new Map<string, JuanaConversationSummary[]>();
    for (const conversation of this.juana.conversations()) {
      const category = conversation.category || 'general';
      const bucket = groups.get(category);
      if (bucket) bucket.push(conversation);
      else groups.set(category, [conversation]);
    }

    const rank = (category: string): number => {
      const index = CATEGORY_ORDER.indexOf(category);
      return index === -1 ? CATEGORY_ORDER.length : index;
    };

    return [...groups.entries()]
      .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
      .map(([category, conversations]) => ({
        category,
        label: this.categoryLabel(category, conversations[0]),
        conversations,
      }));
  }

  /**
   * Rótulo del tema. Si no hay traducción (categoría propuesta por el agente,
   * que no puede estar en los diccionarios), se usa la etiqueta que mandó el
   * backend antes que mostrar el slug crudo.
   */
  private categoryLabel(category: string, sample: JuanaConversationSummary): string {
    const key = `main.juana.categories.${category}`;
    const translated = this.transloco.translate(key);
    if (translated && translated !== key) return translated;
    return sample.categoryLabel || this.transloco.translate('main.juana.categories.general');
  }

  /**
   * El home SIEMPRE abre en la bienvenida (retrato + accesos rápidos), aunque
   * haya una conversación viva: entrar a la página no debe caer en medio de un
   * hilo viejo. Para volver a uno anterior está el historial lateral.
   */
  get hasConversation(): boolean {
    return !this.showWelcome && this.juana.messages().length > 0;
  }

  /**
   * Saludo con el nombre de la cuenta corporativa de la sesión ya cargada
   * (mismo dato que usa el sidebar). Si todavía no llegó, saludo genérico:
   * nunca se inventa un nombre.
   */
  get greeting(): string {
    const company = this.userService.currentUser()?.corporateAccount?.companyName?.trim();
    return company
      ? this.transloco.translate('main.juana.greeting', { company })
      : this.transloco.translate('main.juana.greetingGeneric');
  }

  /** Enter envía; Shift+Enter deja escribir varias líneas en el textarea. */
  onEnter(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.shiftKey) return;
    e.preventDefault();
    this.send();
  }

  async send(text?: string): Promise<void> {
    const value = (text ?? this.input).trim();
    if (!value) return;
    this.input = '';
    // Si se escribe desde la bienvenida pero el hilo activo ya traía mensajes
    // (reanudado de antes), se abre uno nuevo: el mensaje no debe colarse en
    // una conversación que el usuario no tiene a la vista.
    if (this.showWelcome && this.juana.messages().length > 0) {
      await this.juana.newConversation();
    }
    this.showWelcome = false;
    await this.juana.send(value);
  }

  /** Cada tarjeta manda su mensaje predefinido, como si lo hubiera escrito el usuario. */
  runQuickAction(action: QuickAction): void {
    // Si se disparó desde el menú lateral, se cierra: lo que sigue es la
    // respuesta de Juana, y dejarlo abierto la taparía.
    this.historyOpen = false;
    void this.send(this.transloco.translate(action.messageKey));
  }
}
